import { Eyebrow, Reveal } from "./Reveal";

const img = (id, w = 1600) =>
    `https://images.unsplash.com/${id}?q=80&w=${w}&auto=format&fit=crop`;

function ImageCard({ className, image, alt, title, desc, tags }) {
    return (
        <div
            className={`group relative overflow-hidden border border-white/10 bg-elevated transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-white/30 ${className}`}
        >
            <img
                src={image}
                alt={alt}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-transparent" />
            <div className="relative flex h-full flex-col justify-end p-7 lg:p-9">
                <h3 className="font-serif text-2xl text-white lg:text-3xl">
                    {title}
                </h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-zinc-400">
                    {desc}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                    {tags.map((t) => (
                        <span
                            key={t}
                            className="border border-white/15 bg-black/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-300 backdrop-blur"
                        >
                            {t}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}

function SpecCard({ title, items }) {
    return (
        <div className="group flex h-full flex-col justify-between border border-white/10 bg-elevated p-7 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-white/30 lg:p-9">
            <div>
                <h3 className="font-serif text-2xl text-white lg:text-3xl">
                    {title}
                </h3>
                <ul className="mt-6 space-y-3 border-t border-white/10 pt-6">
                    {items.map((it) => (
                        <li
                            key={it}
                            className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-300"
                        >
                            <span className="h-1 w-1 rotate-45 bg-gold" />
                            {it}
                        </li>
                    ))}
                </ul>
            </div>
            <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                DARUNA / FABRICATE & INSTALL
            </div>
        </div>
    );
}

export default function Services() {
    return (
        <section id="layanan" className="py-24 lg:py-32">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                <Reveal>
                    <Eyebrow>Layanan</Eyebrow>
                </Reveal>
                <div className="mt-6 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
                    <Reveal delay={0.05}>
                        <h2 className="max-w-xl font-serif text-4xl font-medium leading-tight text-white lg:text-5xl">
                            Satu tangan untuk kaca, aluminium, &amp; interior.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="max-w-sm text-sm leading-relaxed text-zinc-400">
                            Dari pengukuran di lokasi hingga pemasangan
                            terakhir — semua dikerjakan oleh tim sendiri,
                            tanpa broker.
                        </p>
                    </Reveal>
                </div>

                <div className="mt-14 grid grid-cols-1 gap-4 lg:grid-cols-12">
                    <Reveal className="lg:col-span-7" delay={0}>
                        <ImageCard
                            className="h-[380px] lg:h-[460px]"
                            image={img("photo-1766603636700-e9d80473f40f")}
                            alt="Rumah modern dengan kusen aluminium dan kaca besar"
                            title="Kaca & Aluminium"
                            desc="Kusen, sliding & folding door, jendela, dan kanopi — diukur langsung di lokasi, dipasang rapi dengan finishing presisi."
                            tags={["Kusen", "Sliding", "Folding Door", "Kanopi"]}
                        />
                    </Reveal>
                    <Reveal className="lg:col-span-5" delay={0.08}>
                        <SpecCard
                            title="Fasad & Kaca Structural"
                            items={[
                                "Kaca tempered 5–12 mm",
                                "Silicone structural glazing",
                                "Railing & kaca frameless",
                                "Akesoris stainless",
                            ]}
                        />
                    </Reveal>

                    <Reveal className="lg:col-span-5" delay={0.05}>
                        <ImageCard
                            className="h-[380px] lg:h-[460px]"
                            image={img("photo-1770425910016-4b1b45991db9")}
                            alt="Partisi kaca pada interior kantor modern"
                            title="Partisi & Shower Glass"
                            desc="Partisi kaca frameless untuk kantor dan hunian, serta shower glass yang presisi anti-bocor."
                            tags={["Frameless", "Kantor", "Kamar Mandi"]}
                        />
                    </Reveal>
                    <Reveal className="lg:col-span-7" delay={0.12}>
                        <ImageCard
                            className="h-[380px] lg:h-[460px]"
                            image={img("photo-1724582586495-d050726cf354")}
                            alt="Interior ruang keluarga modern yang elegan"
                            title="Desain Interior"
                            desc="Perencanaan tata ruang, pemilihan material, hingga eksekusi — interior yang menyatu dengan cahaya dan kaca."
                            tags={["Tata Ruang", "Material", "Eksekusi"]}
                        />
                    </Reveal>

                    <Reveal className="lg:col-span-12" delay={0.05}>
                        <a
                            href="#kontak"
                            data-testid="services-kitchen-link"
                            className="group flex items-center justify-between gap-6 border border-white/10 bg-elevated p-7 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-gold/50 lg:p-9"
                        >
                            <div>
                                <h3 className="font-serif text-2xl text-white lg:text-3xl">
                                    Kitchen Set &amp; Furnitur Custom
                                </h3>
                                <p className="mt-2 text-sm text-zinc-400">
                                    Lemari, meja, dan elemen custom — termasuk
                                    aksen kaca dan aluminium di dalamnya.
                                </p>
                            </div>
                            <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold transition-transform duration-300 group-hover:translate-x-2">
                                Konsultasi →
                            </span>
                        </a>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
